import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '@crave/api-client';
import { formatCents, localDate } from '@crave/shared';

/** The mobile unit's timezone. Date pickers default to its calendar day, like the Dashboard. */
export const LOCATION_TIMEZONE = 'America/New_York';
/** Today's service date (YYYY-MM-DD) at the location, not the UTC date. */
export const today = () => localDate(new Date(), LOCATION_TIMEZONE);
/** A readable message for a failed request. */
export const errorText = (e: unknown, fallback = 'Something went wrong.') =>
  e instanceof ApiError ? e.message : fallback;

/** A missing value in words. Money and dates the owner has not entered read "Not set". */
export const NOT_SET = 'Not set';

export const money = (cents: number | null | undefined, empty: string = NOT_SET) =>
  cents === null || cents === undefined ? empty : formatCents(cents);
export const when = (v: string | Date | null | undefined, empty: string = NOT_SET) =>
  v
    ? new Date(v).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : empty;
export const qty = (n: number | null | undefined, unit: string, empty: string = NOT_SET) =>
  n === null || n === undefined
    ? empty
    : `${unit === 'each' ? Math.round(n * 100) / 100 : Math.round(n)} ${unit}`;

/** Which side of a sideways-scrolling box is still hiding something, right now. */
export function tableEdges(box: { scrollLeft: number; clientWidth: number; scrollWidth: number }): {
  left: boolean;
  right: boolean;
} {
  // Browsers measure in fractions of a pixel, so a pixel of slack is not a hidden column.
  return {
    left: box.scrollLeft > 1,
    right: box.scrollLeft + box.clientWidth < box.scrollWidth - 1,
  };
}

/** The height a box needs to show all it holds, kept between a smallest and a largest size. */
export function growHeight(content: number, borders: number, min: number, max: number): number {
  const wanted = Number.isFinite(content) ? content + Math.max(borders, 0) : min;
  return Math.max(min, Math.min(wanted, Math.max(max, min)));
}

/**
 * The tallest a text box that scrolls may be without slicing a line of writing along its edge: the
 * room asked for, trimmed down to whole lines. `chrome` is the height taken up by what is not
 * writing: the borders, plus the padding at one end only, because a box with more in it than it can
 * show keeps the padding blank at one end and runs the writing under the other. If the line height
 * is not a plain number, or not even one line fits, the size asked for is used as it is.
 */
export function wholeLines(max: number, chrome: number, lineHeight: number): number {
  if (!Number.isFinite(max) || !Number.isFinite(chrome) || !Number.isFinite(lineHeight)) return max;
  if (lineHeight <= 0 || chrome < 0) return max;
  const lines = Math.floor((max - chrome) / lineHeight);
  return lines >= 1 ? lines * lineHeight + chrome : max;
}

/*
 * Row buttons (Delete, Print) live in a table's last column. With `stickyActions` that column stays
 * pinned to the right edge on a solid background while the rest scrolls sideways under it, so a
 * phone never has to swipe to reach them. A soft edge shows only while the table really scrolls.
 * In a narrow table the buttons stack and a "Can't delete" note wraps, so the pinned column stays slim.
 *
 * A table too wide for the screen says so instead of hiding columns quietly: a soft fade covers the
 * edge the hidden columns are behind, and a line under it says so: "Swipe to see more" on a phone,
 * "Scroll sideways to see more" where there is a mouse. Both the fade and the line go away at
 * the end of the scroll, so a value cut off by the edge is never read as the whole value. Where the
 * pinned column is on, it already covers the right edge and keeps its own shadow instead of a fade.
 */
const TABLE_CSS = `
.crave-table-box { display: grid; gap: 4px; min-width: 0; }
.crave-table-edge { position: relative; min-width: 0; }
.crave-table-wrap { overflow-x: auto; }
.crave-table-wrap[data-sticky] { container-type: inline-size; }
.crave-table-fade { position: absolute; top: 0; bottom: 0; width: 22px; pointer-events: none;
  opacity: 0; transition: opacity 0.15s ease; }
.crave-table-fade-left { left: 0; background: linear-gradient(to right, var(--crave-table-bg, var(--ivory)), transparent); }
.crave-table-fade-right { right: 0; background: linear-gradient(to left, var(--crave-table-bg, var(--ivory)), transparent); }
.crave-table-edge[data-left] .crave-table-fade-left { opacity: 1; }
.crave-table-edge[data-right] .crave-table-fade-right { opacity: 1; }
.crave-table-edge[data-sticky] .crave-table-fade-right { display: none; }
.crave-table-hint { margin: 0; font-size: 13px; line-height: 1.4; color: var(--ink-soft); }
.crave-table-hint[data-quiet] { visibility: hidden; }
.crave-table-hint-mouse { display: none; }
@media (hover: hover) and (pointer: fine) {
  .crave-table-hint-touch { display: none; }
  .crave-table-hint-mouse { display: inline; }
}
@media (prefers-reduced-motion: reduce) { .crave-table-fade { transition: none; } }
.crave-table-action { position: sticky; right: 0; z-index: 1; white-space: nowrap;
  background: var(--crave-table-bg, var(--ivory)); }
.crave-table-wrap[data-scrolls] .crave-table-action { box-shadow: inset 1px 0 0 var(--crave-hairline, var(--line)), -10px 0 12px -10px rgba(20, 20, 20, 0.28); }
.crave-row-actions { display: flex; gap: 8px; align-items: center; }
.crave-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 14px; line-height: 20px; }
.crave-table th { text-align: left; padding: 10px 12px; border-bottom: 1px solid var(--crave-hairline, var(--line));
  color: var(--crave-label, var(--ink-soft)); font-size: 13px; font-weight: 600; white-space: nowrap; }
.crave-table td { padding: 12px; border-bottom: 1px solid var(--crave-hairline, var(--line)); vertical-align: top; }
.crave-table tbody tr:last-child td { border-bottom: 0; }
@media (hover: hover) { .crave-table tbody tr:hover td { background-color: rgba(20, 20, 20, 0.018); }
  .crave-table tbody tr:hover td.crave-table-action { background-color: var(--crave-table-bg, var(--ivory)); } }
.crave-table-empty { color: var(--ink-soft); }
.crave-row-actions > button { white-space: nowrap; }
@container (max-width: 560px) {
  .crave-table-action { white-space: normal; }
  .crave-table-action > :not(.crave-row-actions) { max-width: 9.5rem; }
  .crave-row-actions { flex-direction: column; align-items: stretch; }
}
`;

/** Several buttons in one table cell. They sit side by side, and stack in a narrow table. */
export function RowActions({ children }: { children: ReactNode }) {
  return <div className="crave-row-actions">{children}</div>;
}

export function Table({
  head,
  rows,
  empty = 'Nothing here yet.',
  rowKeys,
  wrapColumns,
  stickyActions = false,
}: {
  head: ReactNode[];
  rows: ReactNode[][];
  empty?: string;
  /** Column indexes whose cells may wrap (long text). Other cells stay on one line and the table scrolls. */
  wrapColumns?: number[] | undefined;
  /** Stable ids per row, so inputs inside a row stay with their record when the data changes. */
  rowKeys?: string[] | undefined;
  /** The last column holds row buttons: keep it in view at the right edge while the table scrolls. */
  stickyActions?: boolean | undefined;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  // The fades and the pinned column take the colour of whatever the table sits on: a white card or
  // the grey page. Unless a page set --crave-table-bg itself.
  useEffect(() => {
    const box = boxRef.current;
    if (!box || typeof getComputedStyle === 'undefined') return;
    if (getComputedStyle(box).getPropertyValue('--crave-table-bg').trim()) return;
    const bg = backdropColor(box.parentElement);
    if (bg) box.style.setProperty('--crave-table-bg', bg);
  }, []);
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const check = () =>
      setEdges((was) => {
        const now = tableEdges(wrap);
        return was.left === now.left && was.right === now.right ? was : now;
      });
    check();
    wrap.addEventListener('scroll', check, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(check);
    if (observer) {
      observer.observe(wrap);
      const table = wrap.querySelector('table');
      if (table) observer.observe(table);
    }
    return () => {
      wrap.removeEventListener('scroll', check);
      observer?.disconnect();
    };
  }, [rows.length, head.length]);
  const last = head.length - 1;
  const actionClass = (j: number) =>
    stickyActions && j === last ? 'crave-table-action' : undefined;
  return (
    <div className="crave-table-box" ref={boxRef}>
      <style href="crave-table" precedence="default">
        {TABLE_CSS}
      </style>
      <div
        className="crave-table-edge"
        data-sticky={stickyActions ? '' : undefined}
        data-left={edges.left ? '' : undefined}
        data-right={edges.right ? '' : undefined}
      >
        <div
          ref={wrapRef}
          className="crave-table-wrap"
          data-sticky={stickyActions ? '' : undefined}
          data-scrolls={stickyActions && edges.right ? '' : undefined}
        >
          {/* Separate borders: collapsed borders do not travel with a pinned cell in Safari. */}
          <table className="crave-table">
            <thead>
              <tr>
                {head.map((h, i) => (
                  <th key={i} className={actionClass(i)}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={head.length} className="crave-table-empty">
                    {empty}
                  </td>
                </tr>
              )}
              {rows.map((r, i) => (
                <tr key={rowKeys?.[i] ?? i}>
                  {r.map((cell, j) => {
                    const action = actionClass(j);
                    return (
                      <td
                        key={j}
                        className={action}
                        style={{
                          // A pinned action cell sets its own wrapping in TABLE_CSS.
                          ...(action
                            ? {}
                            : wrapColumns?.includes(j) ||
                                (typeof cell === 'string' && cell.length > 40)
                              ? { whiteSpace: 'normal', minWidth: 220, maxWidth: 420 }
                              : { whiteSpace: 'nowrap' }),
                        }}
                      >
                        {cell}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <span className="crave-table-fade crave-table-fade-left" aria-hidden="true" />
        <span className="crave-table-fade crave-table-fade-right" aria-hidden="true" />
      </div>
      {(edges.left || edges.right) && (
        <p className="crave-table-hint" data-quiet={edges.right ? undefined : ''}>
          <span className="crave-table-hint-touch">Swipe to see more</span>
          <span className="crave-table-hint-mouse">Scroll sideways to see more</span>
        </p>
      )}
    </div>
  );
}

/** How loud a status pill is: green when done, amber when it needs watching, red when it needs a person. */
const STATUS_TONE: Record<string, 'ok' | 'warn' | 'alert' | 'muted'> = {
  COMPLETED: 'ok',
  READY: 'ok',
  CANCELED: 'muted',
  NO_SHOW: 'warn',
  MANUAL_REVIEW: 'alert',
  PAYMENT_FAILED: 'alert',
  CANCEL_REQUESTED: 'warn',
};
/** Order statuses as the owner says them. */
export const ORDER_STATUS_WORDS: Record<string, string> = {
  DRAFT: 'Started',
  AWAITING_PAYMENT: 'Waiting for payment',
  PAYMENT_AUTHORIZED: 'Card authorized',
  CONFIRMED: 'Confirmed',
  SCHEDULED: 'Scheduled',
  QUEUED: 'In the queue',
  PREPARING: 'Preparing',
  IN_OVEN: 'In the oven',
  BOXING: 'Boxing',
  READY: 'Ready',
  PICKED_UP: 'Picked up',
  COMPLETED: 'Completed',
  CANCEL_REQUESTED: 'Cancellation requested',
  CANCELED: 'Canceled',
  PAYMENT_FAILED: 'Payment failed',
  REFUNDED: 'Refunded',
  PARTIALLY_REFUNDED: 'Partly refunded',
  NO_SHOW: 'No-show',
  REMAKE: 'Remake',
  MANUAL_REVIEW: 'Waiting for manager review',
};

/** A status or other code in words: known ones from the list, others tidied ("SOME_CODE" to "Some code"). */
export function statusWords(code: string | null | undefined): string {
  if (!code) return '';
  const known = ORDER_STATUS_WORDS[code];
  if (known) return known;
  const t = code.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export function Status({ s }: { s: string }) {
  return (
    <span className="crave-status" data-tone={STATUS_TONE[s]}>
      {statusWords(s)}
    </span>
  );
}

export function Section({
  title,
  children,
  right,
}: {
  title: string;
  children: ReactNode;
  right?: ReactNode;
}) {
  return (
    <section className="crave-section">
      <div className="crave-section-head">
        <h2 className="crave-section-title">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

export function Notice({
  kind = 'info',
  children,
}: {
  kind?: 'info' | 'error' | 'ok';
  children: ReactNode;
}) {
  return (
    <div className="crave-notice" data-kind={kind}>
      {children}
    </div>
  );
}

/** The first solid background colour behind an element, walking up to the page. */
function backdropColor(el: Element | null): string | null {
  for (let node = el; node; node = node.parentElement) {
    const bg = getComputedStyle(node).backgroundColor;
    if (bg && bg !== 'transparent' && !/rgba\([^)]*,\s*0\)$/.test(bg)) return bg;
  }
  return null;
}
